import { LineReadable } from "./line-readable";
import { LineStringReadable } from "./line-string-readable";
import { PolygonReadable } from "./polygon-readable";
import { Polygon2 } from "./polygon2";
import { Polygon3 } from "./polygon3";
import { Range } from "./range";
import { RangeReadable } from "./range-readable";
import type { Vec } from "./vec";
import type { VecLike } from "./vec-like";
import type { Vec2 } from "./vec2";
import type { Vec3 } from "./vec3";

export interface ReadonlyVec{
	readonly vecType:Vec;
	readonly cloneType:Vec; //must be the type returned by clone()

	readonly x:number;
	readonly y:number;
	readonly z?:number;

	clone():Vec;
	toXY():Vec;
	toXYZ(z:number):Vec;

	dot:Vec['dot'];
	distToSq:Vec['distToSq'];
	distTo:Vec['distTo'];
	distToSqXY:Vec['distToSqXY'];
	distToXY:Vec['distToXY'];

	delta(that:VecLike):this['cloneType'];

	eq:Vec['eq'];
	isValid:Vec['isValid'];
	isFinite:Vec['isFinite'];

	readonly tmpA:Vec;
	readonly tmpB:Vec;
	readonly tmpC:Vec;
}

export interface ReadonlyVec2 extends ReadonlyVec{
	readonly isVector2:true;
	readonly vecType:Vec2;
	readonly cloneType:Vec2;

	readonly x:number;
	readonly y:number;

	clone():Vec2;
	toXY():Vec2;
	toXYZ(z:number):Vec3;
	toVec():Vec2;
	toArray():[number,number];

	delta(that:VecLike):this['cloneType'];

	azimuth:Vec2['azimuth'];
}

export interface ReadonlyVec3 extends ReadonlyVec{
	readonly isVector3:true;
	readonly vecType:Vec3;
	readonly cloneType:Vec3;

	readonly x:number;
	readonly y:number;
	readonly z:number;

	clone():Vec3;
	toXY():Vec2;
	toXYZ(z:number):Vec3;
	toVec():Vec3;
	toArray():[number,number,number];

	delta(that:VecLike):this['cloneType'];

	azimuth():number|undefined;
}

export class ReadonlyRange extends RangeReadable{
	public declare readonly min:number;
	public declare readonly max:number;

	public clone(){
		return new Range(this.min,this.max);
	}
}

export class ReadonlyLine<Point extends ReadonlyVec> extends LineReadable<Point>{
	public declare readonly a:Point;
	public declare readonly b:Point;
}

export class ReadonlyLine2<Point extends ReadonlyVec2=ReadonlyVec2> extends ReadonlyLine<Point>{
}

export class ReadonlyLine3<Point extends ReadonlyVec3=ReadonlyVec3> extends ReadonlyLine<Point>{
}

export class ReadonlyLineString<Point extends ReadonlyVec> extends LineStringReadable<Point,ReadonlyArray<Point>>{
	public declare readonly points:ReadonlyArray<Point>;
}

export class ReadonlyLineString2 extends ReadonlyLineString<ReadonlyVec2>{
}

export class ReadonlyLineString3 extends ReadonlyLineString<ReadonlyVec3>{
}

export abstract class ReadonlyPolygon<Point extends ReadonlyVec> extends PolygonReadable<Point,ReadonlyArray<Point>>{
	public declare readonly points:ReadonlyArray<Point>;
}

export class ReadonlyPolygon2 extends ReadonlyPolygon<ReadonlyVec2>{
	public clone(){
		return new Polygon2(this.points.map(p=>p.clone()));
	}
}

export class ReadonlyPolygon3 extends ReadonlyPolygon<ReadonlyVec3>{
	public clone(){
		return new Polygon3(this.points.map(p=>p.clone()));
	}
}
